const NEW_TAB_REL = 'noopener noreferrer';

/**
 * Builds an accessible name that announces opening in a new tab.
 * If `existingAriaLabel` already includes `openInNewTabLabel`, it is returned unchanged.
 */
export function buildOpenInNewTabAriaLabel(
    accessibleName: string,
    openInNewTabLabel: string,
    existingAriaLabel?: string
): string {
    const base = existingAriaLabel?.trim() || accessibleName.trim();
    if (!base) {
        return openInNewTabLabel;
    }

    const normalizedBase = base.toLowerCase();
    const normalizedSuffix = openInNewTabLabel.toLowerCase();
    if (normalizedBase.includes(normalizedSuffix)) {
        return base;
    }

    return `${base} (${openInNewTabLabel})`;
}

export type NewTabLinkAttributes = {
    target: '_blank';
    rel: typeof NEW_TAB_REL;
    'aria-label'?: string;
};

/**
 * Attributes for links that open in a new tab (target, rel, optional aria-label).
 * Pass `accessibleName` (and optionally `openInNewTabLabel`) to enrich aria-label.
 */
export function getNewTabLinkAttributes(options?: {
    accessibleName?: string;
    openInNewTabLabel?: string;
    existingAriaLabel?: string;
}): NewTabLinkAttributes {
    const { accessibleName, openInNewTabLabel, existingAriaLabel } = options ?? {};

    const attrs: NewTabLinkAttributes = {
        target: '_blank',
        rel: NEW_TAB_REL,
    };

    if (accessibleName !== undefined && openInNewTabLabel) {
        attrs['aria-label'] = buildOpenInNewTabAriaLabel(accessibleName, openInNewTabLabel, existingAriaLabel);
    } else if (existingAriaLabel) {
        attrs['aria-label'] = existingAriaLabel;
    }

    return attrs;
}
